import {
  Box,
  Button,
  FormControl,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { colorVariables, customValue } from "../../../utils/colorVariable";
import { IActivateAccount } from "../model/auth";
import { CustomInput } from "../../../components/common/Input/CustomInput";
import { ChangeEvent, FormEvent, useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { activateAccount } from "../../../services/authService";

import { EROUTER } from "../../../utils/enumVariable";
import { validatePassword } from "../../../utils/validate";
import { AuthLeftContent } from "../../../components/layouts/AuthLeftContent";
import dayjs from "dayjs";

const INITIAL_STATE: IActivateAccount = {
  password: "",
  password_confirmation: "",
  token: "",
};

export const ActiveAcount = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const breakpoints = {
    isMobile: useMediaQuery(theme.breakpoints.down("sm")),
    isLarge: useMediaQuery(theme.breakpoints.up("lg")),
    isExtraLarge: useMediaQuery(theme.breakpoints.up("xl")),
  };
  const [errors, setErrors] = useState<Partial<IActivateAccount>>({});
  const [activeAccountState, setActiveAccountState] =
    useState<IActivateAccount>(INITIAL_STATE);

  
  // Extraire le token de l'URL
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('token')
    console.log('Extracted Token:', token); // Log pour vérifier le token extrait
    if (token) {
      setActiveAccountState(prevState => ({ ...prevState, token }));
    } else {
      console.error('No token found in URL');
    }
  }, []);

  // Handlers
  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = event.target;
      setActiveAccountState((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<IActivateAccount> = {};

    if (!validatePassword(activeAccountState.password)) {
      newErrors.password =
        "Password must be at least 8 characters, with uppercase, numbers, and special characters";
    }

    if (!validatePassword(activeAccountState.password_confirmation)) {
      newErrors.password_confirmation =
        "Password must be at least 8 characters, with uppercase, numbers, and special characters";
    }

    if (
      !validatePassword(activeAccountState.password) &&
      !validatePassword(activeAccountState.password_confirmation) &&
      activeAccountState.password.trim() !==
        activeAccountState.password_confirmation.trim()
    ) {
      newErrors.password = "Password doesn't match!";
      newErrors.password_confirmation = "Password doesn't match!";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [activeAccountState.password, activeAccountState.password_confirmation]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!validateForm()) {
        toast.error("Please fix the errors in the form");
        return;
      }

      try {
        const response = await activateAccount({
          password: activeAccountState.password,
          password_confirmation: activeAccountState.password_confirmation,
          token: activeAccountState.token,
        });

        if (response) {
          toast.success("Account activated successful!");
          navigate(EROUTER.SIGNIN);
          setErrors({});
        } else {
          throw new Error("Activation failed");
        }
      } catch (error) {
        toast.error((error as Error).message);
      }
    },
    [validateForm, activeAccountState, navigate]
  );

  return (
    <Box
      component={Paper}
      height={{ sm: "80vh", xs: "100vh" }}
      width={breakpoints.isExtraLarge ? "50vw" : "inherit"}
      mx={breakpoints.isExtraLarge || breakpoints.isMobile ? "auto" : 20}
      my={{ sm: 10 }}
      borderRadius={customValue.borderRadius}
      elevation={5}
      display={"flex"}
      flexDirection={"row"}
    >
      {breakpoints.isLarge && <AuthLeftContent />}
      <Box
        width={breakpoints.isLarge ? "50%" : "100%"}
        display={"flex"}
        alignItems={"flex-start"}
        justifyContent={"center"}
        flexDirection={"column"}
        px={10}
        height={"auto"}
        gap={errors ? 1 : 2}
        component={"form"}
        onSubmit={handleSubmit}
      >
        <Typography
          variant="h4"
          color={colorVariables.primary}
          fontWeight={"lighter"}
        >
          Account Activation
        </Typography>
        {/* Password Input */}
        <FormControl fullWidth error={!!errors.password}>
          <CustomInput
            label="Password"
            name="password"
            placeholder="Enter your password"
            type="password"
            value={activeAccountState.password}
            onChange={handleInputChange}
            error={errors.password}
          />
        </FormControl>
        <FormControl fullWidth error={!!errors.password}>
          <CustomInput
            label="Password confirmation"
            name="password_confirmation"
            placeholder="Enter your password"
            type="password"
            value={activeAccountState.password_confirmation}
            onChange={handleInputChange}
            error={errors.password_confirmation}
          />
        </FormControl>

        <Button
          fullWidth
          sx={{
            background: colorVariables.gradient_primary,
            color: colorVariables.white,
          }}
          type="submit"
        >
          Send
        </Button>
        <Typography variant="caption">
          © {dayjs(new Date()).format("YYYY")} Accord Knits SA, Antananarivo,
          Madagascar
        </Typography>
      </Box>
    </Box>
  );
};
